import React from 'react';
import { Phone, FileText, ClipboardCheck, ArrowRight } from 'lucide-react';

const HowItWorksSection = ({ isDark }) => {
  const phases = [
    {
      id: 'pre',
      label: 'Pre-Consultation',
      icon: Phone,
      color: 'sky',
      items: [
        'AI voice agent handles calls',
        'Captures symptoms automatically',
        'Books appointments',
        'Triage + prioritization'
      ]
    },
    {
      id: 'during',
      label: 'During Consultation',
      icon: FileText,
      color: 'emerald',
      items: [
        'Real-time transcription',
        'AI clinical reasoning',
        'ICD-10 auto coding',
        'Drug + lab suggestions',
        'Red flag detection'
      ]
    },
    {
      id: 'post',
      label: 'Post-Consultation',
      icon: ClipboardCheck,
      color: 'violet',
      items: [
        'Auto-generated notes',
        'EHR sync',
        'Prescription + lab orders',
        'Follow-up automation',
        'Patient engagement'
      ]
    }
  ];

  const getColorClasses = (color, isDark) => {
    const colors = {
      sky: {
        bg: isDark ? 'bg-sky-900/40' : 'bg-sky-50',
        icon: 'text-sky-500',
        border: isDark ? 'border-sky-600' : 'border-sky-400',
        accent: 'bg-sky-500'
      },
      emerald: {
        bg: isDark ? 'bg-emerald-900/40' : 'bg-emerald-50',
        icon: 'text-emerald-500',
        border: isDark ? 'border-emerald-600' : 'border-emerald-400',
        accent: 'bg-emerald-500'
      },
      violet: {
        bg: isDark ? 'bg-violet-900/40' : 'bg-violet-50',
        icon: 'text-violet-500',
        border: isDark ? 'border-violet-600' : 'border-violet-400',
        accent: 'bg-violet-500'
      }
    };
    return colors[color];
  };

  return (
    <section 
      id="workflow" 
      className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}
      data-testid="how-it-works-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header - Minimal */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
            isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
          }`}>
            How It Works
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-semibold mb-5 tracking-tight ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            One AI System. Every Step of Care.
          </h2>
          <p className={`text-lg max-w-xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            No switching systems. No manual entry. No missed steps.
          </p>
        </div>

        {/* Phase cards - Cleaner */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {phases.map((phase, index) => {
            const colors = getColorClasses(phase.color, isDark);
            const Icon = phase.icon;
            
            return (
              <div 
                key={phase.id}
                className="relative"
                data-testid={`phase-${phase.id}`}
              >
                {/* Connector arrow */}
                {index < phases.length - 1 && (
                  <div className="hidden md:flex absolute top-20 -right-4 lg:-right-5 z-10">
                    <ArrowRight className={`w-6 h-6 ${isDark ? 'text-slate-700' : 'text-stone-300'}`} />
                  </div>
                )}

                <div className={`p-6 lg:p-8 rounded-2xl border-t-2 h-full transition-all hover:-translate-y-1 ${colors.border} ${
                  isDark ? 'bg-slate-800/50' : 'bg-white shadow-sm'
                }`}>
                  {/* Icon */}
                  <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-5`}>
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>

                  {/* Phase number - subtle */}
                  <span className={`text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-500' : 'text-slate-400'
                  }`}>
                    Phase {index + 1}
                  </span>

                  {/* Title */}
                  <h3 className={`text-xl font-semibold mt-1 mb-4 ${
                    isDark ? 'text-white' : 'text-slate-900'
                  }`}>
                    {phase.label}
                  </h3>

                  {/* Items - cleaner list */}
                  <ul className="space-y-2.5">
                    {phase.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2.5">
                        <div className={`w-1.5 h-1.5 ${colors.accent} rounded-full mt-2 flex-shrink-0`} />
                        <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom tagline - minimal */}
        <div className="mt-14 text-center">
          <div className={`inline-flex items-center gap-3 px-5 py-2.5 rounded-full ${
            isDark ? 'bg-slate-800' : 'bg-stone-100'
          }`}>
            <div className="flex -space-x-1">
              <div className="w-2.5 h-2.5 bg-sky-500 rounded-full" />
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full" />
              <div className="w-2.5 h-2.5 bg-violet-500 rounded-full" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Continuous workflow from first contact to completed care
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
